import * as types from './mutations-types';
import * as formHelper from '@phoenix/helpers/form-helper';
import { find, get, set, omit } from 'lodash-es';
import { clientApi } from '@phoenix/clientApi';

const initialState = {
  groups: {},
  deliveryCountries: [],
  billingCountries: [],
  isLoading: true,
  isSaving: false,
  reference: null,
  boutiques: [],
  blockId: null,
  taxIncluded: false,

  hasGiftWrap: false,
  hasGiftMessage: false,
  giftMessage: null,
  giftMessageFont: null,

  selectedDeliveryMode: null,
  deliveryLoading: false,
  deliveryAddress: null,
  selectedBoutiqueId: null,
  deliveryStepConfirmed: false,

  billingAddress: null,
  billingStepConfirmed: false,

  contactInformations: {
    phone: null,
    email: null,
    preferredContact: [],
  },
  contactStepConfirmed: false,

  additionalInformations: '',

  newsletter: false,

  hasSubmitted: false,
};

const getters = {
  canSubmit(state) {
    return state.deliveryStepConfirmed && state.billingStepConfirmed && state.contactStepConfirmed;
  },

  shouldDisplaySameAsDeliveryBtn(state) {
    return state.selectedDeliveryMode !== 'pickup';
  },

  tracking(state) {
    return {
      items: [state.reference.tracking],
      shipping_tier: state.selectedDeliveryMode ?? '', // eslint-disable-line
    };
  },

  hasGiftMessage(state) {
    return state.hasGiftMessage;
  },
};

const actions = {
  async sendCheckoutRequest({ state, commit, getters }, captchaToken = null) {
    commit(types.LIGHT_CHECKOUT_SUBMIT_REQUEST);

    const data = {
      blockId: state.blockId,
      modelReference: state.reference.modelReference,
      deliveryMode: state.selectedDeliveryMode,
      boutiqueAddress: find(state.boutiques, { swseBoutiqueCode: state.selectedBoutiqueId }) ?? null,
      deliveryAddress: state.deliveryAddress !== null ? formHelper.convertFormDataToData(state.deliveryAddress) : null,
      billingAddress: state.billingAddress !== null ? formHelper.convertFormDataToData(state.billingAddress) : null,
      contactInformations: state.contactInformations,
      additionalInformations: state.additionalInformations,
      newsletter: state.newsletter,
      hasGiftWrap: state.hasGiftWrap,
      hasGiftMessage: state.hasGiftMessage,
      giftMessage: state.giftMessage,
      giftMessageFont: state.giftMessageFont,
      captchaToken,
    };

    try {
      await clientApi.post(window.route('api.lightecom.checkout'), data);

      window.tracking.pushEvent(
        window.tracking.getLightEcomSubmitTracking(getters.tracking.items, getters.tracking.shipping_tier)
      );

      commit(types.LIGHT_CHECKOUT_SUBMIT_SUCCESS);
    } catch (error) {
      commit(types.LIGHT_CHECKOUT_SUBMIT_FAILURE, error);
    }
  },

  changeGiftMessage({ commit }, newValue) {
    commit(types.LIGHT_CHECKOUT_SET_GIFT_MESSAGE, newValue);
  },

  changeChkbGiftWrap({ commit, getters }, newValue) {
    window.tracking.pushEvent(
      window.tracking.getLightEcomGiftWrappingTracking(getters.tracking.items, getters.tracking.shipping_tier)
    );
    commit(types.LIGHT_CHECKOUT_SET_GIFT_WRAP, newValue);
  },

  changeChkbGiftMessage({ commit, getters }, newValue) {
    window.tracking.pushEvent(
      window.tracking.getLightEcomGiftMessageTracking(getters.tracking.items, getters.tracking.shipping_tier)
    );
    commit(types.LIGHT_CHECKOUT_SET_GIFT_HAS_MESSAGE, newValue);
  },

  changeGiftMessageFont({ commit }, font) {
    commit(types.LIGHT_CHECKOUT_SET_GIFT_MESSAGE_FONT, font);
  },

  removeGiftMessage({ commit }) {
    commit(types.LIGHT_CHECKOUT_SET_GIFT_MESSAGE, null);
    commit(types.LIGHT_CHECKOUT_SET_GIFT_HAS_MESSAGE, false);
  },
};

const mutations = {
  [types.LIGHT_CHECKOUT_INIT](state, initialisationData) {
    state.blockId = initialisationData.blockId ?? state.blockId;
    state.deliveryCountries = initialisationData.deliveryCountries ?? state.deliveryCountries;
    state.billingCountries = initialisationData.billingCountries ?? state.billingCountries;
    state.groups = initialisationData.groups ?? state.groups;
    state.reference = initialisationData.reference ?? state.reference;
    state.boutiques = initialisationData.boutiques ?? state.boutiques;
    state.isLoading = false;
    state.taxIncluded = initialisationData.taxIncluded;
  },

  // GIFT
  [types.LIGHT_CHECKOUT_SET_GIFT_WRAP](state, isWrapped) {
    state.hasGiftWrap = isWrapped;
  },

  [types.LIGHT_CHECKOUT_SET_GIFT_HAS_MESSAGE](state, hasMessage) {
    state.hasGiftMessage = hasMessage;
  },

  [types.LIGHT_CHECKOUT_SET_GIFT_MESSAGE](state, newMessage) {
    state.giftMessage = newMessage;
  },

  [types.LIGHT_CHECKOUT_SET_GIFT_MESSAGE_FONT](state, font) {
    state.giftMessageFont = font;
  },

  // DELIVERY

  [types.LIGHT_CHECKOUT_SET_DELIVERY_MODE](state, mode) {
    state.deliveryStepConfirmed = false;
    state.deliveryAddress = null;
    state.billingStepConfirmed = false;
    state.billingAddress = null;
    state.selectedBoutiqueId = null;
    state.selectedDeliveryMode = mode;
  },

  [types.LIGHT_CHECKOUT_SET_DELIVERY_ADDRESS](state, address) {
    state.deliveryAddress = address;
    state.billingAddress = null;
    state.billingStepConfirmed = false;
    state.deliveryStepConfirmed = true;
  },

  [types.LIGHT_CHECKOUT_EDIT_DELIVERY_ADDRESS](state) {
    state.deliveryAddress = null;
    state.selectedBoutiqueId = null;
    state.deliveryStepConfirmed = false;
  },

  [types.LIGHT_CHECKOUT_SET_SELECTED_BOUTIQUE](state, boutiqueId) {
    state.selectedBoutiqueId = boutiqueId;
    state.deliveryStepConfirmed = true;
  },

  // BILLING

  [types.LIGHT_CHECKOUT_SET_BILLING_ADDRESS](state, address) {
    set(state, 'billingAddress', address);
    state.billingStepConfirmed = true;
  },

  [types.LIGHT_CHECKOUT_EDIT_BILLING_ADDRESS](state) {
    state.billingAddress = null;
    state.billingStepConfirmed = false;
  },

  [types.LIGHT_CHECKOUT_SET_BILLING_ADDRESS_SAME_AS_DELIVERY_ADDRESS](state) {
    set(state, 'billingAddress', omit(state.deliveryAddress, ['deliveryPhone']));
    state.billingStepConfirmed = true;
  },

  // CONTACT INFORMATIONS

  [types.LIGHT_CHECKOUT_EDIT_CONTACT_INFORMATIONS](state) {
    set(state.contactInformations, 'phone', null);
    set(state.contactInformations, 'email', null);
    set(state.contactInformations, 'preferredContact', []);

    state.contactStepConfirmed = false;
  },

  [types.LIGHT_CHECKOUT_SET_CONTACT_INFORMATIONS](state, contactInformations) {
    set(state.contactInformations, 'phone', contactInformations.phone);
    set(state.contactInformations, 'email', contactInformations.email);
    set(state.contactInformations, 'preferredContact', contactInformations.preferredContact);

    state.contactStepConfirmed = true;
  },

  // ADDITIONAL INFORMATIONS

  [types.LIGHT_CHECKOUT_SET_ADDITIONAL_INFORMATIONS](state, additionalInformations) {
    set(state, 'additionalInformations', additionalInformations);
  },

  // FINAL STEP

  [types.LIGHT_CHECKOUT_SET_NEWSLETTER](state, newsletter) {
    state.newsletter = newsletter;
  },

  // SUBMIT

  [types.LIGHT_CHECKOUT_SUBMIT_REQUEST](state) {
    state.isSaving = true;
  },

  [types.LIGHT_CHECKOUT_SUBMIT_SUCCESS](state) {
    state.isSaving = false;
    state.hasSubmitted = true;
  },

  [types.LIGHT_CHECKOUT_SUBMIT_FAILURE](state, error) {
    if (error) {
      window.Phoenix.sentry.captureException(error);
      window.Phoenix.toaster.notify({
        type: 'danger',
        text: get(error, 'response.data.message', error.message),
      });
    }

    state.isSaving = false;
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
